import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import toast from 'react-hot-toast'
import flowRuleApi from '@/api/config/flowRuleApi'
import { useQueryClient } from '@tanstack/react-query'
import { getApiErrorMessage } from '@/helpers'
import { queryKeys } from '@/constant/common'
import { deleteFlowRuleWithChildren } from '@/features/config/hooks/hydraulic/flowRuleCascade'
import useFlowRuleCascadeCheck from '@/features/config/hooks/hydraulic/useFlowRuleCascadeCheck'
import { FLOW_RULE_DEFAULTS } from '../../HydraulicRule/FlowRulePanel/constants'
import type { RuleSection } from '../../HydraulicRule/FlowRulePanel/RuleRail'
import useSearchFlowRule from '../../../hooks/hydraulic/flowRule/useSearchFlowRule'
import type { HydraulicTreeRow } from './types'

interface UseFlowRuleActionsArgs {
  authInterfaceId: unknown
  isViewOnly: boolean
  setGridData: Dispatch<SetStateAction<HydraulicTreeRow[]>>
  onRefetch?: () => Promise<any>
  t: (key: string, options?: any) => string
}

export interface SectionPopoverState {
  pipeId: number
  section: Exclude<RuleSection, 'alarm'>
  flowRuleId: number
  interfaceId: number
  name: string
  /** Metric l/m — a new valve defaults to the pipe it feeds. */
  capacity: number
  anchorPosition: { top: number; left: number }
}

export interface AlarmDialogState {
  pipeId: number
  flowRuleId: number
  interfaceId: number
  name: string
  capacity: number
  anchorPosition: { top: number; left: number }
}

/** The flow rule a pipe carries: creating it, removing it, and its workspaces. */
const useFlowRuleActions = ({
  authInterfaceId,
  isViewOnly,
  setGridData,
  onRefetch,
  t,
}: UseFlowRuleActionsArgs) => {
  // One section popover at a time, anchored to the chip that opened it.
  const [sectionPopover, setSectionPopover] =
    useState<SectionPopoverState | null>(null)
  const [creatingRuleFor, setCreatingRuleFor] = useState<number | null>(null)
  const [deletingRule, setDeletingRule] = useState<{
    pipeId: number
    flowRuleId: number
    interfaceId: string
    name: string
  } | null>(null)
  const [deleteRunning, setDeleteRunning] = useState(false)
  const [alarmDialogPipe, setAlarmDialogPipe] =
    useState<AlarmDialogState | null>(null)

  // The alarm chip needs each rule's active flag, which the pipe payload does
  // not carry. Keyed on queryKeys.flowRule, so every rule save refreshes it.
  const { data: ruleListData } = useSearchFlowRule(Number(authInterfaceId))
  const ruleActiveMap = useMemo(() => {
    const map: Record<string, boolean> = {}
    ;(ruleListData?.list ?? []).forEach((r: any) => {
      map[String(r.id)] = Boolean(r.active)
    })
    return map
  }, [ruleListData])

  const handleOpenAlarm = useCallback(
    (pipe: HydraulicTreeRow, anchorEl: HTMLElement | null) => {
      // The rect is captured NOW: cells are recycled freely, so holding the
      // element itself risks measuring a detached node (rect 0,0) and the
      // popup landing in the top-left corner.
      const rect = anchorEl?.getBoundingClientRect()
      setAlarmDialogPipe({
        pipeId: pipe.id,
        flowRuleId: Number(pipe.flowRuleId),
        interfaceId:
          Number((pipe as any).interfaceId) || Number(authInterfaceId),
        name: String(pipe.description ?? ''),
        capacity: Number(pipe.capacity ?? 0),
        // The panel hangs LEFTWARDS from the chip's right edge, so this is
        // `rect.right`, not `rect.left`.
        anchorPosition: rect
          ? { top: rect.bottom + 6, left: rect.right }
          : { top: window.innerHeight / 3, left: window.innerWidth / 2 + 280 },
      })
    },
    [authInterfaceId],
  )

  const handleOpenSection = useCallback(
    (
      pipe: HydraulicTreeRow,
      section: Exclude<RuleSection, 'alarm'>,
      anchorEl: HTMLElement | null,
    ) => {
      const rect = anchorEl?.getBoundingClientRect()
      setSectionPopover((prev) =>
        prev?.pipeId === pipe.id && prev?.section === section
          ? null
          : {
              pipeId: pipe.id,
              section,
              flowRuleId: Number(pipe.flowRuleId),
              interfaceId:
                Number((pipe as any).interfaceId) || Number(authInterfaceId),
              name: String(pipe.description ?? ''),
              capacity: Number(pipe.capacity ?? 0),
              // Right edge: the panel hangs leftwards from the chip.
              anchorPosition: rect
                ? { top: rect.bottom + 6, left: rect.right }
                : {
                    top: window.innerHeight / 3,
                    left: window.innerWidth / 2 + 300,
                  },
            },
      )
    },
    [authInterfaceId],
  )

  const handleRequestDeleteRule = useCallback(
    (pipe: HydraulicTreeRow) => {
      if (isViewOnly) return
      setDeletingRule({
        pipeId: pipe.id,
        flowRuleId: Number(pipe.flowRuleId),
        interfaceId: String((pipe as any).interfaceId || authInterfaceId),
        name: String(pipe.description ?? ''),
      })
    },
    [authInterfaceId, isViewOnly],
  )

  // After a delete the panel closes and the pipe's cell flips back to "Add"
  // immediately: the grid data is patched here, without waiting for the pipe
  // list refetch that confirms it.
  const handleRuleDeleted = useCallback(
    (pipeId: number) => {
      setSectionPopover(null)
      setGridData((prev) =>
        prev
          .filter((row) => !(row.isRuleRow && row.parentId === pipeId))
          .map((row) =>
            row.id === pipeId ? { ...row, flowRuleId: 0 as any } : row,
          ),
      )
    },
    [setGridData],
  )

  /**
   * SCRUM-1032: what the rule would take with it, looked up while the
   * confirmation is on screen — fresh on every opening. The same list is
   * handed to the delete, so what is removed is what the user was shown.
   */
  const {
    children: deletingRuleChildren,
    childrenForDelete,
    loading: loadingRuleChildren,
    blockReason,
  } = useFlowRuleCascadeCheck(
    deletingRule?.interfaceId ?? Number(authInterfaceId),
    deletingRule?.flowRuleId,
  )

  const queryClient = useQueryClient()

  const handleConfirmDeleteRule = useCallback(async () => {
    if (!deletingRule) return
    // A valve or sensor a water source uses would go with the rule.
    const blocked = blockReason()
    if (blocked) {
      toast.error(blocked)
      setDeletingRule(null)
      return
    }
    setDeleteRunning(true)
    try {
      // The API refuses to delete a rule that still has valves or sensors on
      // it, so they go first — in that order, since a valve points at a sensor.
      await deleteFlowRuleWithChildren(
        deletingRule.interfaceId,
        String(deletingRule.flowRuleId),
        childrenForDelete,
      )
      toast.success(t('deleteFlowRuleSuccess'))
      handleRuleDeleted(deletingRule.pipeId)
      setDeletingRule(null)
      onRefetch?.()
    } catch (error: any) {
      // The success path refetches the pipes itself.
      queryClient.invalidateQueries([queryKeys.hydraulic])
      toast.error(getApiErrorMessage(error))
    } finally {
      // Success or not: the cascade may have removed some valves before it
      // stopped, and the panel and the valve grids read these lists.
      queryClient.invalidateQueries([queryKeys.flowRule])
      queryClient.invalidateQueries([queryKeys.flowValve])
      queryClient.invalidateQueries([queryKeys.flowSensor])
      setDeleteRunning(false)
    }
  }, [
    deletingRule,
    childrenForDelete,
    blockReason,
    handleRuleDeleted,
    onRefetch,
    queryClient,
    t,
  ])

  /**
   * "Add" creates the rule immediately, seeded from FLOW_RULE_DEFAULTS, and
   * opens the panel on the sensors and valves.
   *
   * The defaults leave both deviation limits at 100% and automatic action
   * off, so a rule added by mistake monitors and reports but cannot raise an
   * alarm or stop a pump — which is what makes one-click creation safe.
   */
  const handleCreateRule = useCallback(
    async (pipe: HydraulicTreeRow) => {
      if (isViewOnly) return
      setCreatingRuleFor(pipe.id)
      try {
        await flowRuleApi.create({
          ...FLOW_RULE_DEFAULTS,
          interfaceId:
            Number((pipe as any).interfaceId) || Number(authInterfaceId),
          pipeId: pipe.id,
        })
        // The spinner holds until the refetch lands, at which point the four
        // Configure buttons appear on the row — that is the "done" signal.
        await onRefetch?.()
      } catch (error: any) {
        toast.error(getApiErrorMessage(error))
      } finally {
        setCreatingRuleFor(null)
      }
    },
    [authInterfaceId, isViewOnly, onRefetch, t],
  )

  return {
    sectionPopover,
    setSectionPopover,
    creatingRuleFor,
    deletingRule,
    setDeletingRule,
    deleteRunning,
    deletingRuleChildren,
    loadingRuleChildren,
    alarmDialogPipe,
    setAlarmDialogPipe,
    ruleActiveMap,
    handleOpenAlarm,
    handleOpenSection,
    handleRequestDeleteRule,
    handleConfirmDeleteRule,
    handleCreateRule,
  }
}

export default useFlowRuleActions
